import React, { useCallback, useState, lazy, Suspense } from 'react';
import cn from 'classnames';
import { GatsbyImage } from 'gatsby-plugin-image';
import Title from 'components/UI/Title';
import LazyImage from 'components/UI/LazyImage';
import { getImagesWithArtDirection } from 'utils/images';
import HeroVideo from './HeroVideo';
import { useStateModal } from 'utils/hooks';
import { useCertificatesInfo } from 'staticQueries/certificates-info';
import { withErrorBoundary } from 'components/ErrorBoundary';

import * as styles from './Intro.module.scss';

const CertificateModal = lazy(() => import('components/Modals/CertificateModal'));

function Intro({ title, icons, video, certificateIDs, imgAlt = '', gatsbyImage }) {
  const certificates = useCertificatesInfo(certificateIDs);
  const [state, setState] = useState();
  const isSsr = typeof window === 'undefined';
  const images = gatsbyImage?.desktop && getImagesWithArtDirection(gatsbyImage);
  const isDesktop = !isSsr && window.innerWidth > 1024;
  const { modalState, showModal, hideModal } = useStateModal();

  const callModal = useCallback(modalData => {
    setState(modalData);
    showModal(modalData?.modalName);
  }, []);

  return (
    <section className={styles.introWrapper}>
      <div className={styles.intro}>
        <GatsbyImage
          alt={imgAlt}
          image={images}
          className={styles.picture}
          imgClassName={styles.pictureImage}
          loading="eager"
        />
        {video && <HeroVideo video={video} isDesktop={isDesktop} />}
        <div className={styles.container}>
          <div className={styles.layout}>
            <div className={styles.content}>
              <Title tag="h1" color="white" className={styles.title}>
                {title}
              </Title>
              <div className={styles.logos}>
                {icons.map(({ imgSrc, alt }) => (
                  <div key={alt} className={styles.logoWrapper}>
                    <LazyImage src={imgSrc} alt={alt} className={styles.logo} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.iconsLayout}>
            <div className={styles.icons}>
              {certificates.map(({ images, link, modalData }) => {
                const { preview, modalWindow: imgForModal, alt } = images;
                const shouldShowModal = Boolean(modalData?.title);

                return (
                  <div key={preview} className={cn(styles.icon, link && styles.link)}>
                    <div
                      className={styles.iconWrapper}
                      onClick={() => {
                        if (shouldShowModal) {
                          callModal({
                            ...modalData,
                            img: imgForModal ?? preview,
                            link,
                          });
                        }
                      }}
                      role={shouldShowModal ? 'button' : undefined}
                    >
                      <LazyImage src={preview} alt={alt} className={styles.iconImg} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <Suspense fallback={null}>
        <CertificateModal isOpen={modalState.isOpen} closeModalCallback={hideModal} {...state} />
      </Suspense>
    </section>
  );
}

Intro.displayName = 'Intro';

export default withErrorBoundary(Intro);
